import { resolveCodDisplayCurrency } from '@modules/CODPayments/utils/resolveCurrencyFromApi';
import { formatCurrencyAmount, useCurrency } from '@modules/Orders/shared/hooks/useCurrency';
import { SectionBox } from '@shared/components/ui/SectionBox';
import { faBuilding, faCalendar, faDollarSign, faWallet } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { formatDateWithTime } from '@shared/utils/FormatUtils';
import { useTranslation } from 'react-i18next';
import { PayoutDetail } from '../../models';

interface PayoutHeaderProps {
  payout: PayoutDetail;
  isFetching?: boolean;
}

export const PayoutHeader = ({ payout, isFetching = false }: PayoutHeaderProps) => {
  const { t } = useTranslation('codPayoutDetail');
  const teamCurrency = useCurrency();
  const displayCurrency = resolveCodDisplayCurrency(payout.currency, teamCurrency);
  const isNegative = payout.total_amount < 0;

  const formattedDate = formatDateWithTime(payout.payout_date);

  const cards = [
    {
      title: t('header.client', 'Cliente'),
      value: payout.client.name,
      icon: faBuilding,
      iconBgClass: 'bg-card-border',
      iconTextClass: 'text-icons',
    },
    {
      title: t('header.payout_date', 'Fecha Liquidación'),
      value: formattedDate,
      icon: faCalendar,
      iconBgClass: 'bg-card-border',
      iconTextClass: 'text-icons',
    },
    {
      title: t('header.total', 'Total'),
      value: formatCurrencyAmount(payout.total_amount, displayCurrency),
      icon: faDollarSign,
      iconBgClass: 'bg-card-border',
      iconTextClass: isNegative ? 'text-destructive' : 'text-icons',
      valueClassName: isNegative ? 'text-destructive' : '',
    },
    {
      title: t('header.wallet_transaction', 'Transacción Wallet'),
      value: payout.wallet_transaction_id ? `#${payout.wallet_transaction_id}` : t('header.no_transaction', 'N/A'),
      icon: faWallet,
      iconBgClass: 'bg-card-border',
      iconTextClass: 'text-icons',
    },
  ];

  return (
    <div className="mb-6">
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        {cards.map((card, index) => (
          <SectionBox key={index} className="">
            <div className="flex items-center justify-between">
              <div className="flex-1">
                <h3 className="text-sm text-subtext mb-1">{card.title}</h3>
                <p className={`text-md text-title ${card.valueClassName || ''}`}>
                  {isFetching ? (
                    <span className="inline-flex items-center gap-2">
                      <span className="animate-pulse">...</span>
                    </span>
                  ) : (
                    card.value
                  )}
                </p>
              </div>
              <div className={`w-8 h-8 rounded-md flex items-center justify-center flex-shrink-0`}>
                <FontAwesomeIcon icon={card.icon} className={`w-4 h-4 ${card.iconTextClass}`} />
              </div>
            </div>
          </SectionBox>
        ))}
      </div>
    </div>
  );
};
